/**
 * NotificationBell — Notification Centre (v109.9.51 redesign)
 *
 * Design goals:
 *   • Bell button is round (per user directive).
 *   • Panel uses the app's glass-morphism aesthetic — a frosted white/slate
 *     surface with a soft indigo tint. Header is NOT a solid indigo bar;
 *     it's a glass strip with an accent rail so it reads well in both
 *     light AND dark mode.
 *   • Semantic notification chips use theme tokens so they're visible in
 *     either mode without extra dark-mode rules.
 *   • View toggle + filters use the shared soft-pill style.
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, X, Filter, Inbox, MailOpen, AlertCircle, Wallet, Plane, Truck, ClipboardList, FileText, Megaphone, UserCheck, Users, ShoppingCart, ArrowRight, Trash2, MessageSquare } from "lucide-react";
import api, { formatError } from "../lib/api";
import { playDing } from "../lib/sounds";
import { toast } from "sonner";

const fmtRel = (s) => {
  if (!s) return "";
  try {
    const d = new Date(s);
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h ago`;
    const day = Math.floor(h / 24);
    if (day < 7) return `${day}d ago`;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  } catch { return s; }
};

// Notification type → visual config.
// Colours are chosen so they render well in BOTH light and dark modes.
// We wrap chip surfaces with `chipTone` — a token-based token that CSS
// (line 2037-2072 of index.css) already remaps for dark mode.
const TYPE_CONFIG = {
  order_created:                 { tone: "emerald", label: "Order",       icon: ShoppingCart,    refKey: "order_id",       highlight: "order"        },
  order_updated:                 { tone: "emerald", label: "Order",       icon: ShoppingCart,    refKey: "order_id",       highlight: "order"        },
  invoice_created:               { tone: "blue",    label: "Invoice",     icon: FileText,        refKey: "invoice_id",     highlight: "invoice"      },
  invoice_overdue:               { tone: "rose",    label: "Overdue",     icon: AlertCircle,     refKey: "invoice_id",     highlight: "invoice"      },
  credit_note_issued:            { tone: "amber",   label: "Credit Note", icon: FileText,        refKey: "credit_note_id", highlight: "credit_note"  },
  dispatch_created:              { tone: "cyan",    label: "Dispatch",    icon: Truck,           refKey: "dispatch_id",    highlight: "dispatch"     },
  lead_call_request_created:     { tone: "amber",   label: "Call",        icon: Users,           refKey: "request_id",     highlight: "call_request" },
  lead_call_request_resolved:    { tone: "emerald", label: "Call",        icon: Users,           refKey: "request_id",     highlight: "call_request" },
  lead_call_request_overridden:  { tone: "orange",  label: "Call",        icon: Users,           refKey: "request_id",     highlight: "call_request" },
  mapping_request:               { tone: "amber",   label: "Mapping",     icon: Users,           refKey: "client_id",      highlight: "client"       },
  doc_log_updated:               { tone: "violet",  label: "Docs",        icon: ClipboardList,   refKey: "client_id",      highlight: "client"       },
  announcement:                  { tone: "pink",    label: "Announce",    icon: Megaphone,       refKey: null,             highlight: "default"      },
  new_module_granted:            { tone: "indigo",  label: "Access",      icon: UserCheck,       refKey: "modules",        highlight: "default"      },
  travel_advance_submitted:      { tone: "sky",     label: "Travel Adv",  icon: Wallet,          refKey: "advance_id",     highlight: "default"      },
  travel_advance_approved:       { tone: "emerald", label: "Travel Adv",  icon: Wallet,          refKey: "advance_id",     highlight: "default"      },
  travel_advance_disbursed:      { tone: "blue",    label: "Travel Adv",  icon: Wallet,          refKey: "advance_id",     highlight: "default"      },
  travel_booking_submitted:      { tone: "sky",     label: "Travel",      icon: Plane,           refKey: "booking_id",     highlight: "default"      },
  travel_booking_approved:       { tone: "emerald", label: "Travel",      icon: Plane,           refKey: "booking_id",     highlight: "default"      },
  travel_booking_booked:         { tone: "blue",    label: "Travel",      icon: Plane,           refKey: "booking_id",     highlight: "default"      },
  travel_reimbursement_paid:     { tone: "emerald", label: "Reimburse",   icon: Wallet,          refKey: "booking_id",     highlight: "default"      },
  daily_expense_submitted:       { tone: "sky",     label: "Expense",     icon: Wallet,          refKey: "expense_id",     highlight: "default"      },
  daily_expense_reimbursed:      { tone: "emerald", label: "Expense",     icon: Wallet,          refKey: "expense_id",     highlight: "default"      },
  chat_message:                  { tone: "violet",  label: "Chat",        icon: MessageSquare,   refKey: "thread_id",      highlight: "default"      },
  default:                       { tone: "slate",   label: "Info",        icon: Inbox,           refKey: null,             highlight: "default"      },
};

// Tailwind classes keyed by tone — chip surface + icon colour.
// Uses `-100` bg + `-700` text which the global CSS (index.css §7) already
// re-tints in dark mode so both themes look right without extra overrides.
const TONE_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-700 dark:text-emerald-300",
  blue:    "bg-blue-100 text-blue-700 dark:text-blue-300",
  rose:    "bg-rose-100 text-rose-700 dark:text-rose-300",
  amber:   "bg-amber-100 text-amber-700 dark:text-amber-300",
  cyan:    "bg-cyan-100 text-cyan-700 dark:text-cyan-300",
  orange:  "bg-orange-100 text-orange-700 dark:text-orange-300",
  violet:  "bg-violet-100 text-violet-700 dark:text-violet-300",
  pink:    "bg-pink-100 text-pink-700 dark:text-pink-300",
  indigo:  "bg-indigo-100 text-indigo-700 dark:text-indigo-300",
  sky:     "bg-sky-100 text-sky-700 dark:text-sky-300",
  slate:   "bg-slate-100 text-slate-700 dark:text-slate-300",
};

// v109.9.77 — Approval-needed notification types are excluded from the bell.
// They live exclusively in the "Approval Requests" panel next to the bell so
// notifications stay informational only. Requests, submissions and any
// "pending your action" events belong in the Approval Requests inbox.
const APPROVAL_NOTIFICATION_TYPES = new Set([
  // Travel
  "travel_advance_submitted",
  "travel_advance_step_pending",
  "travel_booking_submitted",
  "travel_reimbursement_submitted",
  // Expenses / Payroll
  "daily_expense_submitted",
  "incentive_submitted",
  // CRM
  "mapping_request",
  "contact_delete_request",
  "contact_transfer_request",
  "lead_call_request_created",
  // HR
  "comp_off_request",
  "leave_request_submitted",
  "exit_interview_submitted",
  "employee_submitted",
  "offboarding_pending",
  "training_request",
  // Announcements / Documents
  "announcement_pending",
  "document_approval_needed",
  "doc_stage_pending",
  // Generic
  "advance_request",
  "user_request",
]);

// Any type ending in these suffixes is treated as an approval-needed event
// and hidden from the bell. Belt-and-suspenders alongside the explicit set.
const APPROVAL_SUFFIXES = ["_submitted", "_request", "_pending", "_approval_needed"];

function isApprovalNotification(type) {
  if (!type) return false;
  if (APPROVAL_NOTIFICATION_TYPES.has(type)) return true;
  return APPROVAL_SUFFIXES.some((s) => type.endsWith(s));
}

const cfgFor = (t) => TYPE_CONFIG[t] || TYPE_CONFIG.default;
const chipFor = (tone) => TONE_CLASSES[tone] || TONE_CLASSES.slate;

const moduleFromUrl = (url) => {
  if (!url) return "general";
  if (url.startsWith("/chat")) return "chat";
  if (!url.startsWith("/m/")) return "general";
  return url.split("/m/", 1)[1] ? url.split("/m/")[1].split("/")[0].split("?")[0] : "general";
};

const MODULE_LABEL = {
  general: "General",
  sales_orders: "Orders",
  invoice_generation: "Invoices",
  dispatch_orders: "Dispatch",
  crm_clients: "CRM",
  sales_crm_report: "CRM Reporting",
  client_documentation: "Documentation",
  hr_leaves: "Leaves",
  accounts_advances: "Travel Advance",
  accounts_claims: "Claims",
  hr_daily_report: "Daily Report",
  it_tickets: "IT Tickets",
  travel_booking: "Travel Booking",
  daily_expense: "Daily Expense",
  announcements: "Announcements",
  attendance: "Attendance",
  tasks: "Tasks",
  inventory_management: "Inventory",
  vendor_management: "Vendors",
  product_issuance: "Product Issuance",
  service_order_management: "Service Orders",
  chat: "Chat",
};

export default function NotificationBell({ className = "" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [view, setView] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const ref = useRef(null);
  const lastUnread = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 100 } });
      // v109.9.77 — Strip approval-needed items; those live in Approval Requests.
      const items = (data.items || []).filter((n) => !isApprovalNotification(n.type));
      setItems(items);
      const kept = items.filter((n) => !n.read_at).length;
      if (lastUnread.current !== null && kept > lastUnread.current) {
        try { playDing(); } catch { /* */ }
      }
      lastUnread.current = kept;
      setUnread(kept);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => { if (open) { setView("all"); setModuleFilter("all"); } }, [open]);

  const moduleCounts = useMemo(() => {
    const m = {};
    items.forEach((n) => {
      const k = moduleFromUrl(n.url);
      m[k] = m[k] || { total: 0, unread: 0 };
      m[k].total += 1;
      if (!n.read_at) m[k].unread += 1;
    });
    return m;
  }, [items]);

  const sortedModules = useMemo(() => {
    return Object.entries(moduleCounts)
      .sort((a, b) => b[1].unread - a[1].unread || b[1].total - a[1].total)
      .map(([key, c]) => ({ key, label: MODULE_LABEL[key] || key, ...c }));
  }, [moduleCounts]);

  const filteredItems = useMemo(() => {
    let arr = items;
    if (view === "unread") arr = arr.filter((n) => !n.read_at);
    if (moduleFilter !== "all") arr = arr.filter((n) => moduleFromUrl(n.url) === moduleFilter);
    return arr;
  }, [items, view, moduleFilter]);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const clearAll = async () => {
    if (!items.length) return;
    if (!window.confirm(`Clear all ${items.length} notifications? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete("/notifications/clear-all");
      toast.success(`Cleared ${data.deleted ?? items.length} notification(s)`);
      setItems([]);
      setUnread(0);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const openItem = (n) => {
    if (!n.read_at) markRead(n.id);
    if (!n.url) return;
    setOpen(false);
    const cfg = cfgFor(n.type);
    const refId = cfg.refKey ? (n.ref || {})[cfg.refKey] : null;
    if (refId && typeof refId === "string") {
      const sep = n.url.includes("?") ? "&" : "?";
      navigate(`${n.url}${sep}highlight=${encodeURIComponent(refId)}&hl_color=${cfg.highlight}`);
    } else {
      navigate(n.url);
    }
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      {/* ROUND bell button — glass-morphism idle, soft indigo tint on hover */}
      <button
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 rounded-full grid place-items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-indigo-50 dark:hover:bg-indigo-500/15 hover:border-indigo-300 dark:hover:border-indigo-500/40 active:scale-95 hover:-translate-y-0.5 hover:shadow-md hover:shadow-indigo-200/40 dark:hover:shadow-indigo-500/20 transition-all duration-150"
        data-testid="notif-bell"
        aria-label="Notifications"
        data-keep-color
      >
        <Bell
          size={17}
          strokeWidth={2}
          className={unread > 0 ? "text-indigo-600 dark:text-indigo-300 animate-[wiggle_1s_ease-in-out]" : "text-slate-600 dark:text-slate-300"}
        />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-extrabold grid place-items-center ring-2 ring-white dark:ring-slate-900 shadow-sm animate-pulse"
            data-testid="notif-unread-count"
            data-keep-color
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Solid backdrop — no blur; starts below the top bar so the
              header stays crisp. v109.9.87 */}
          <div
            className="fixed left-0 right-0 bottom-0 bg-slate-900/55 dark:bg-black/70 z-40"
            style={{ top: 56 }}
            onClick={() => setOpen(false)}
            data-testid="notif-backdrop"
            aria-hidden="true"
          />
          <div
            className="absolute right-0 mt-2 w-[440px] max-w-[calc(100vw-1rem)] rounded-2xl overflow-hidden z-50 animate-in fade-in-50 slide-in-from-top-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.30),0_8px_20px_-8px_rgba(15,23,42,0.15)] dark:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7),0_8px_20px_-8px_rgba(0,0,0,0.5)]"
            data-testid="notif-panel"
            data-keep-color
            style={{ backgroundColor: "var(--pk-notif-bg, #ffffff)" }}
          >
          {/* Solid opaque header — clean white / slate-900, no glass or transparency */}
          <div
            className="relative flex items-center justify-between px-4 py-3 overflow-hidden border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
            data-keep-color
          >
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500 dark:bg-indigo-400" aria-hidden="true" data-keep-color />
            <div className="relative flex items-center gap-2.5 pl-2">
              <div className="w-10 h-10 rounded-full grid place-items-center bg-indigo-100 dark:bg-indigo-500/30 ring-1 ring-indigo-200 dark:ring-indigo-400/50" data-keep-color>
                <Bell size={17} className="text-indigo-600 dark:text-indigo-300" strokeWidth={2.2} />
              </div>
              <div>
                <div className="text-[13.5px] font-bold leading-tight text-slate-900 dark:text-white">Notification Centre</div>
                <div className="text-[10.5px] mt-0.5 font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  <span className="text-indigo-600 dark:text-indigo-300 font-extrabold">{unread}</span> unread · {items.length} total
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="relative w-8 h-8 grid place-items-center rounded-full hover:bg-indigo-100 dark:hover:bg-indigo-500/25 active:scale-90 transition-all"
              aria-label="Close"
              data-testid="notif-close"
              data-keep-color
            >
              <X size={15} className="text-slate-600 dark:text-slate-300" strokeWidth={2} />
            </button>
          </div>

          {/* View toggle — solid slate-50 / slate-800 background, no transparency */}
          <div
            className="flex items-center gap-1 p-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700"
            data-keep-color
          >
            {[
              { k: "all", label: "All", icon: Inbox, count: items.length },
              { k: "unread", label: "Unread", icon: MailOpen, count: unread },
              { k: "by-module", label: "By Module", icon: Filter, count: sortedModules.length },
            ].map(({ k, label, icon: Icon, count }) => (
              <button
                key={k}
                onClick={() => { setView(k); if (k !== "by-module") setModuleFilter("all"); }}
                className={`flex-1 py-1.5 px-2 text-[11.5px] font-bold rounded-lg flex items-center justify-center gap-1.5 transition-all active:scale-95 ${
                  view === k
                    ? "bg-white dark:bg-slate-900 text-indigo-700 dark:text-indigo-300 shadow-sm ring-1 ring-indigo-200 dark:ring-indigo-500/40"
                    : "text-slate-600 dark:text-slate-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
                data-testid={`notif-view-${k}`}
                data-keep-color
              >
                <Icon size={12} strokeWidth={2.2} /> {label}
                {count > 0 && (
                  <span
                    className={`ml-0.5 text-[10px] mono px-1 py-px rounded ${
                      view === k
                        ? "bg-indigo-100 dark:bg-indigo-500/25 text-indigo-700 dark:text-indigo-200"
                        : "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300"
                    }`}
                    data-keep-color
                  >
                    {count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* By-Module chip filter */}
          {view === "by-module" && (
            <div
              className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex flex-wrap gap-1.5 max-h-[110px] overflow-y-auto"
              data-testid="notif-module-chips"
              data-keep-color
            >
              <button
                onClick={() => setModuleFilter("all")}
                className={`text-[10.5px] px-2.5 py-1 rounded-full border font-bold transition-all active:scale-95 ${
                  moduleFilter === "all"
                    ? "bg-indigo-600 text-white border-indigo-700 shadow-sm"
                    : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-500/40"
                }`}
                data-testid="notif-mod-all"
                data-keep-color
              >
                All · {items.length}
              </button>
              {sortedModules.map((m) => (
                <button
                  key={m.key}
                  onClick={() => setModuleFilter(m.key)}
                  className={`text-[10.5px] px-2.5 py-1 rounded-full border flex items-center gap-1 font-bold transition-all active:scale-95 ${
                    moduleFilter === m.key
                      ? "bg-indigo-600 text-white border-indigo-700 shadow-sm"
                      : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-500/40"
                  }`}
                  data-testid={`notif-mod-${m.key}`}
                  data-keep-color
                >
                  {m.label} · {m.total}
                  {m.unread > 0 && (
                    <span className={`mono ${moduleFilter === m.key ? "text-white/90" : "text-rose-600 dark:text-rose-400"}`}>
                      · {m.unread}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Actions bar */}
          <div
            className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-[10.5px]"
            data-keep-color
          >
            <div className="text-slate-500 dark:text-slate-400 font-semibold">{filteredItems.length} shown</div>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-bold bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400 shadow-sm active:scale-95 transition-all"
                  data-testid="notif-mark-all-read"
                  data-keep-color
                >
                  <CheckCheck size={12} strokeWidth={2.5} /> Mark all read
                </button>
              )}
              {items.length > 0 && (
                <button
                  onClick={clearAll}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-bold bg-white dark:bg-slate-800 text-rose-600 dark:text-rose-300 border border-rose-200 dark:border-rose-500/40 hover:bg-rose-50 dark:hover:bg-rose-500/15 shadow-sm active:scale-95 transition-all"
                  data-testid="notif-clear-all"
                  data-keep-color
                >
                  <Trash2 size={12} strokeWidth={2.5} /> Clear all
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div
            className="max-h-[480px] overflow-y-auto bg-white dark:bg-slate-900"
            data-testid="notif-list"
            data-keep-color
          >
            {filteredItems.length === 0 && (
              <div className="text-center py-12 text-xs">
                <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 mx-auto mb-3 grid place-items-center" data-keep-color>
                  <Inbox size={22} className="text-slate-400 dark:text-slate-500" />
                </div>
                <div className="font-semibold text-slate-500 dark:text-slate-300">{view === "unread" ? "All caught up!" : "Nothing here yet"}</div>
                <div className="text-[10.5px] text-slate-400 dark:text-slate-500 mt-0.5">
                  {view === "unread" ? "No unread notifications." : "No notifications match this filter."}
                </div>
              </div>
            )}
            {filteredItems.map((n) => {
              const cfg = cfgFor(n.type);
              const Icon = cfg.icon || Inbox;
              const mod = moduleFromUrl(n.url);
              const modLabel = MODULE_LABEL[mod] || mod;
              const chip = chipFor(cfg.tone);
              return (
                <button
                  key={n.id}
                  onClick={() => openItem(n)}
                  className={`group w-full text-left px-3.5 py-3 border-b border-slate-100 dark:border-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-500/15 transition-all flex items-start gap-2.5 ${
                    !n.read_at
                      ? "bg-indigo-50 dark:bg-indigo-500/15 border-l-[3px] border-l-indigo-500 dark:border-l-indigo-400"
                      : "bg-white dark:bg-slate-900 border-l-[3px] border-l-transparent"
                  }`}
                  data-testid={`notif-item-${n.id}`}
                  data-keep-color
                >
                  <div
                    className={`shrink-0 w-9 h-9 grid place-items-center rounded-full ${chip} ring-1 ring-white/40 dark:ring-white/10 shadow-sm group-hover:scale-110 transition-transform`}
                    data-keep-color
                  >
                    <Icon size={15} strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className={`text-[12.5px] truncate ${!n.read_at ? "font-extrabold text-slate-900 dark:text-white" : "font-semibold text-slate-700 dark:text-slate-200"}`}>{n.title}</div>
                      <div className="shrink-0 text-[10px] mono text-slate-400 dark:text-slate-500">{fmtRel(n.created_at)}</div>
                    </div>
                    {n.body && <div className="text-[11px] text-slate-600 dark:text-slate-300 mt-0.5 line-clamp-2 whitespace-pre-wrap break-words">{n.body}</div>}
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <span className={`text-[9.5px] uppercase tracking-wider font-extrabold px-1.5 py-0.5 rounded-full ${chip}`} data-keep-color>{cfg.label}</span>
                      <span className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold">{modLabel}</span>
                      {n.url && (
                        <span className="text-[10px] text-indigo-700 dark:text-indigo-300 ml-auto font-bold flex items-center gap-0.5 group-hover:translate-x-0.5 transition-transform">
                          Open <ArrowRight size={10} />
                        </span>
                      )}
                    </div>
                  </div>
                  {!n.read_at && <span className="shrink-0 w-2 h-2 mt-1.5 rounded-full bg-rose-500 animate-pulse" data-keep-color />}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div
            className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-300 gap-2"
            data-keep-color
          >
            <span className="flex items-center gap-1.5 truncate">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" data-keep-color /> Auto-refreshing every 45s
            </span>
            <button
              onClick={() => { setOpen(false); navigate("/"); }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-bold bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400 shadow-sm active:scale-95 transition-all shrink-0"
              data-testid="notif-go-dashboard"
              data-keep-color
            >
              Go to Dashboard <ArrowRight size={11} strokeWidth={2.5} />
            </button>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
